import { AccessorFn, ColumnDef, ColumnHelper } from "@tanstack/react-table";
import { ScheduleFrequency } from "PFApp/analytics/types/export_analytics";
import { sortingDateFn } from "PFComponents/table/table.utils";
import React from "react";
import { useTranslation } from "react-i18next";

import { Report, Schedule, Template } from "../../../types/reports";
import css from "../../reports.module.scss";
import { ActionColumn } from "../column/action_column";
import { DateColumn } from "../column/date_column";
import { RepeatColumn } from "../column/repeat_column";
import { ReportNameColumn } from "../column/report_name_column";
import { TemplateColumn } from "../column/template_column";

enum ColumnId {
  ReportName = "reportNameColumn",
  Repeat = "repeatColumn",
  Template = "templateColumn",
  NextSchedule = "nextScheduleColumn",
  CreationDate = "creationDateColumn",
  Action = "actionColumn"
}

type ColumnGenerator<TVALUE = any> = (columnHelper: ColumnHelper<Report>) => ColumnDef<Report, TVALUE>;

type UseReportsColumnsGenerator = {
  reportNameColumn: ColumnGenerator<string>;
  repeatColumn: ColumnGenerator<ScheduleFrequency>;
  templateColumn: ColumnGenerator<Template>;
  nextScheduleColumn: ColumnGenerator<Schedule | undefined>;
  creationDateColumn: ColumnGenerator<string>;
  actionColumn: ColumnGenerator<Report>;
};

export const useReportsColumnsGenerator = (): UseReportsColumnsGenerator => {
  const { t } = useTranslation("insights", { keyPrefix: "reports.table" });
  return {
    reportNameColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor((row) => row.name, {
        id: ColumnId.ReportName,
        cell: (info) => <ReportNameColumn reportName={info.getValue()} />,
        header: () => <span className={css.tableHeader}>{t("reportNameColumn")}</span>,
        size: 250
      }),
    repeatColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor((row) => row.schedule?.repeat, {
        id: ColumnId.Repeat,
        cell: (info) => <RepeatColumn repeat={info.getValue()} />,
        header: () => <span className={css.tableHeader}>{t("repeatColumn")}</span>,
        size: 80
      }),
    templateColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor((row) => row.template, {
        id: ColumnId.Template,
        cell: (info) => (
          <TemplateColumn templateName={info.getValue().name} templateId={info.getValue().id} />
        ),
        header: () => <span className={css.tableHeader}>{t("templateColumn")}</span>
      }),
    nextScheduleColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor<AccessorFn<Report>, Schedule | undefined>((row) => row.schedule, {
        id: ColumnId.NextSchedule,
        cell: (info) => <DateColumn date={info.getValue()?.nextRun} />,
        header: () => <span className={css.tableHeader}>{t("nextScheduleColumn")}</span>,
        size: 120,
        sortingFn: (rowA, rowB) =>
          sortingDateFn(
            rowA.original?.schedule?.nextRun ? new Date(rowA.original?.schedule?.nextRun) : undefined,
            rowB.original?.schedule?.nextRun ? new Date(rowB.original?.schedule?.nextRun) : undefined
          )
      }),
    creationDateColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor<AccessorFn<Report>, string>((row) => row.template.createdAt, {
        id: ColumnId.CreationDate,
        cell: (info) => <DateColumn date={info.getValue()} />,
        header: () => <span className={css.tableHeader}>{t("creationDateColumn")}</span>,
        size: 120,
        sortingFn: (rowA, rowB) =>
          sortingDateFn(
            new Date(rowA.original.template.createdAt),
            new Date(rowB.original.template.createdAt)
          )
      }),
    actionColumn: (columnHelper: ColumnHelper<Report>) =>
      columnHelper.accessor((row) => row, {
        id: ColumnId.Action,
        cell: (row) => <ActionColumn row={row.getValue()} />,
        header: () => <span className={css.tableHeader}></span>,
        size: 80,
        enableSorting: false
      })
  };
};
